import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import FilmStripLoader from '../components/FilmStripLoader';

const WatchEpisode = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasActiveSubscription } = useAuth();
  const [series, setSeries] = useState(null);
  const [episodeInfo, setEpisodeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const iframeRef = useRef(null);
  const hasTrackedView = useRef(false);
  
  // Get episode info from navigation state
  const episodeData = location.state || {};

  useEffect(() => {
    if (!hasActiveSubscription) {
      navigate('/subscriptions');
      return;
    }
    fetchEpisodeInfo();
    
    // Fetch series info if we have seriesId from navigation state
    if (episodeData.seriesId) {
      fetchSeriesInfo(episodeData.seriesId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasActiveSubscription]);

  useEffect(() => {
    // Security: Disable common inspection methods
    const handleKeyDown = (e) => {
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) ||
          (e.ctrlKey && ['U', 'S'].includes(e.key))) {
        e.preventDefault();
        return false;
      }
    };
    
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  const fetchSeriesInfo = async (seriesId) => {
    try {
      const res = await axios.get(`/api/series/${seriesId}`);
      setSeries(res.data);
    } catch (error) {
      console.error('Error fetching series:', error);
    }
  };

  const fetchEpisodeInfo = async () => {
    try {
      console.log('Fetching episode with token:', token);
      console.log('Episode data from navigation:', episodeData);
      
      // Detect mobile devices
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // On mobile, use the optimized embed route for better mobile support
        const embedUrl = `/api/stream/embed/${token}`;
        if (iframeRef.current) {
          iframeRef.current.src = embedUrl;
        }
        // Set episode info with success flag for mobile, including episode data from state
        setEpisodeInfo({ 
          success: true, 
          isMobile: true,
          ...episodeData // Include seriesId, seasonNumber, episodeNumber from navigation state
        });
      } else {
        // On desktop, get direct embed URL from backend
        const embedRes = await axios.get(`/api/stream/${token}`);
        if (embedRes.data.success && embedRes.data.embedUrl) {
          // Set iframe src directly to Bunny Stream embed URL (no wrapper iframe)
          if (iframeRef.current) {
            iframeRef.current.src = embedRes.data.embedUrl;
          }
        }
        // Merge episode data from navigation state with API response
        setEpisodeInfo({
          ...embedRes.data,
          ...episodeData // Include seriesId, seasonNumber, episodeNumber from navigation state
        });
      }
      
      console.log('Episode info set successfully');
    } catch (error) {
      console.error('Error fetching episode:', error);
      if (error.response?.status === 403) {
        navigate('/subscriptions');
      }
    } finally {
      setLoading(false);
      // Hide loader after a short delay for smooth transition
      setTimeout(() => {
        setShowLoader(false);
      }, 600);
    }
  };

  const saveWatchHistory = async () => {
    try {
      if (episodeInfo) {
        // Note: We can't track actual video progress due to iframe cross-origin restrictions
        // So we just track that the user watched this episode
        await axios.post(`/api/series/${episodeInfo.seriesId}/season/${episodeInfo.seasonNumber}/episode/${episodeInfo.episodeNumber}/history`, { timestamp: 0 });
        console.log('Watch history saved for episode:', episodeInfo.episodeNumber);
      }
    } catch (error) {
      console.error('Error saving history:', error);
    }
  };

  // Track initial view only once when video loads
  useEffect(() => {
    // Only track if we have episode info with required fields
    if (!episodeInfo || !episodeInfo.seriesId || !episodeInfo.seasonNumber || !episodeInfo.episodeNumber || hasTrackedView.current) return;

    // Wait a few seconds to ensure they actually started watching
    const trackTimer = setTimeout(() => {
      saveWatchHistory();
      hasTrackedView.current = true;
    }, 5000); // Track after 5 seconds of watching

    return () => clearTimeout(trackTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeInfo]);

  if (!hasActiveSubscription) {
    return null;
  }

  if (showLoader) {
    return (
      <div className={loading ? '' : 'fade-out-loader'}>
        <FilmStripLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Bunny Stream Video Player - Direct embed, no wrapper iframe */}
      <div 
        style={{ position: 'relative', paddingTop: '56.25%', width: '100%', height: 0 }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <iframe
          ref={iframeRef}
          src=""
          title="Video Player"
          loading="lazy"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
          allowFullScreen
          frameBorder="0"
          scrolling="no"
          onLoad={() => {
            console.log('Video player loaded');
          }}
          onError={(e) => {
            console.error('Video player error:', e);
          }}
        />
      </div>

      {/* Video Info */}
      {(series || episodeData.seriesTitle) && (
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {series?.title || episodeData.seriesTitle}
              </h1>
              {episodeInfo && episodeInfo.seasonNumber && episodeInfo.episodeNumber && (
                <p className="text-gray-300">
                  Saison {episodeInfo.seasonNumber}, Épisode {episodeInfo.episodeNumber}
                </p>
              )}
            </div>
            <Link
              to={`/series/${series?._id || episodeData.seriesId}`}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition"
            >
              Retour à la série
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchEpisode;
